import React, { useContext, useMemo, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { Button, Dropdown, Input, Message } from 'semantic-ui-react';

import { kebabCase } from 'lodash';

import { register } from '../i18n';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import '../css/RedeemLicenseCodeButton.less';

const t = register('RedeemLicenseCodeButton');

const LICENSE_LEVEL = {
  USER: 'user',
  DISTRICT: 'district',
  SCHOOL: 'school'
};

const getProductDisplayName = (element) => (
  element.licenseElementEntityFriendlyName
  || element.licenseElementEntityName
  || element.licenseElementEntityCode
);

const getSchoolInstitutionOptions = (userManager) => (
  userManager.institutionDropdownOptions.filter(
    (option) => option.type?.toLowerCase() !== 'district'
  )
);

const REDEEM_LICENSE_MODAL_HEIGHT = 650;
const REDEEM_LICENSE_MODAL_VIEWPORT_OFFSET = 48;
const REDEEM_LICENSE_MODAL_MIN_TOP = 24;

const RedeemLicenseCodeButton = observer((props = {}) => {
  const { libraryManager, userManager } = useContext(MobXProviderContext);
  const { onLicenseRedeemed } = props;

  const GenericModal = useMemo(() => SatCoreComponent('GenericModal'), []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [licenseCode, setLicenseCode] = useState('');
  const [validatedCode, setValidatedCode] = useState('');
  const [licenseLevel, setLicenseLevel] = useState(null);
  const [licenseElements, setLicenseElements] = useState([]);
  const [selectedInstitutionId, setSelectedInstitutionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const schoolOptions = getSchoolInstitutionOptions(userManager);
  const isSchoolLicense = licenseLevel?.toLowerCase() === LICENSE_LEVEL.SCHOOL;
  const showSchoolDropdown = isSchoolLicense && schoolOptions.length > 1;
  const showLicenseDetails = validatedCode && licenseCode.trim() === validatedCode;

  const resetModalState = () => {
    setLicenseCode('');
    setValidatedCode('');
    setLicenseLevel(null);
    setLicenseElements([]);
    setSelectedInstitutionId(null);
    setIsLoading(false);
    setErrorMsg('');
  };

  const handleOpenModal = () => {
    resetModalState();
    setIsModalOpen(true);
  };

  const handleDismiss = () => {
    setIsModalOpen(false);
    resetModalState();
  };

  const clearLicenseDetails = () => {
    setValidatedCode('');
    setLicenseLevel(null);
    setLicenseElements([]);
    setSelectedInstitutionId(null);
  };

  const handleLicenseCodeChange = (event) => {
    const nextCode = event.target.value;
    setLicenseCode(nextCode);
    setErrorMsg('');
    if (validatedCode && nextCode.trim() !== validatedCode) {
      clearLicenseDetails();
    }
  };

  const applySchoolSelectionForLevel = (level) => {
    if (level?.toLowerCase() !== LICENSE_LEVEL.SCHOOL) {
      setSelectedInstitutionId(null);
      return;
    }
    if (schoolOptions.length === 1) {
      setSelectedInstitutionId(schoolOptions[0].value);
    } else {
      setSelectedInstitutionId(null);
    }
  };

  const getInstitutionIdForActivate = () => {
    if (!isSchoolLicense) {
      return null;
    }
    if (schoolOptions.length === 1) {
      return schoolOptions[0].value;
    }
    return selectedInstitutionId;
  };

  const handleActivate = async () => {
    const trimmedCode = licenseCode.trim();
    const institutionId = getInstitutionIdForActivate();

    if (isSchoolLicense && showSchoolDropdown && !institutionId) {
      setErrorMsg(t('schoolRequired'));
      return;
    }

    setIsLoading(true);
    setErrorMsg('');

    try {
      const activateResponse = await libraryManager.activateOpenLicenseCode(trimmedCode, institutionId);
      if (!activateResponse) {
        setErrorMsg(t('activateRequestFailed'));
        return;
      }

      if (!activateResponse.licenseActivated) {
        setErrorMsg(activateResponse.statusMessage || t('activateRequestFailed'));
        return;
      }

      await onLicenseRedeemed?.();
      handleDismiss();
    } catch (error) {
      console.error(error);
      setErrorMsg(t('activateRequestFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidate = async () => {
    const trimmedCode = licenseCode.trim();
    if (!trimmedCode) {
      setErrorMsg(t('licenseCodeRequired'));
      return;
    }

    setIsLoading(true);
    setErrorMsg('');
    clearLicenseDetails();

    try {
      const validateResponse = await libraryManager.validateOpenLicenseCode(trimmedCode);
      if (!validateResponse) {
        setErrorMsg(t('validationRequestFailed'));
        return;
      }

      if (!validateResponse.licenseAvailableForUse) {
        setErrorMsg(validateResponse.statusMessage || t('validationRequestFailed'));
        return;
      }

      const elementsResponse = await libraryManager.viewLicenseElementsByCode(trimmedCode);
      if (!elementsResponse) {
        setErrorMsg(t('licenseElementsRequestFailed'));
        return;
      }

      const level = elementsResponse.licenseLevel || null;
      setValidatedCode(trimmedCode);
      setLicenseLevel(level);
      setLicenseElements(elementsResponse.data || []);
      applySchoolSelectionForLevel(level);
    } catch (error) {
      console.error(error);
      setErrorMsg(t('validationRequestFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = async (event, actionButtonData = {}) => {
    const confirmButtonLabel = showLicenseDetails ? t('confirm') : t('continue');
    const confirmed = actionButtonData?.className?.includes?.(kebabCase(confirmButtonLabel));
    if (!confirmed) {
      handleDismiss();
      return;
    }
    if (showLicenseDetails) {
      await handleActivate();
    } else {
      await handleValidate();
    }
  };

  const renderLicenseProducts = () => {
    if (!licenseElements.length) {
      return null;
    }

    return (
      <div className='redeem-license-code-modal-products'>
        <div className='redeem-license-code-modal-section-text'>
          {t('productsIncludedDescription')}
        </div>
        <ul className='redeem-license-code-modal-products-list'>
          {licenseElements.map((element) => {
            const label = getProductDisplayName(element);
            const key = element.licenseElementId || element.licenseElementEntityId || label;
            return (
              <li key={key} className='redeem-license-code-modal-products-item'>
                {label}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderSchoolDropdown = () => {
    if (!showSchoolDropdown) {
      return null;
    }

    return (
      <div className='redeem-license-code-modal-school'>
        <div className='redeem-license-code-modal-section-text'>
          {t('selectSchoolDescription')}
        </div>
        <Dropdown
          className='redeem-license-code-modal-school-dropdown'
          disabled={isLoading}
          onChange={(_event, data) => {
            setSelectedInstitutionId(data.value);
            setErrorMsg('');
          }}
          options={schoolOptions.map((option) => ({
            key: option.value,
            text: option.text,
            value: option.value
          }))}
          placeholder={t('selectSchoolPlaceholder')}
          search
          selection
          value={selectedInstitutionId || ''} />
      </div>
    );
  };

  const renderRedeemLicenseCodeModalBody = () => (
    <div className='redeem-license-code-modal-body'>
      <div className='redeem-license-code-modal-body-header'>{t('redeemLicenseCodeModalBodyDescription')}</div>
      <div className='redeem-license-code-modal-body-label'>{t('redeemLicenseCodeModalBodyLabel')}</div>
      <Input
        className='redeem-license-code-modal-body-input'
        disabled={isLoading}
        onChange={handleLicenseCodeChange}
        type='text'
        value={licenseCode} />
      {showLicenseDetails && (
        <>
          {renderLicenseProducts()}
          {renderSchoolDropdown()}
        </>
      )}
      {!!errorMsg && (
        <Message className='redeem-license-code-modal-message' content={errorMsg} error />
      )}
    </div>
  );

  if (!libraryManager.allowRedeemLicenseCodeButton) {
    return null;
  }

  const modalHeight = Math.min(
    REDEEM_LICENSE_MODAL_HEIGHT,
    window.innerHeight - REDEEM_LICENSE_MODAL_VIEWPORT_OFFSET
  );
  const modalTop = Math.max(
    REDEEM_LICENSE_MODAL_MIN_TOP,
    (window.innerHeight - modalHeight) / 2
  );
  const confirmButtonLabel = showLicenseDetails ? t('confirm') : t('continue');
  const isConfirmDisabled = !licenseCode.trim()
    || (showLicenseDetails && showSchoolDropdown && !selectedInstitutionId);

  return (
    <>
      <Button className='redeem-license-code-button' onClick={handleOpenModal} primary>
        {t('redeemLicenseCodeLabel')}
      </Button>
      <GenericModal
        bodyMsg={renderRedeemLicenseCodeModalBody}
        buttonKeyCancelLabel={t('cancel')}
        buttonKeyConfirmLabel={confirmButtonLabel}
        className='redeem-license-code-modal'
        disableConfirmButton={isConfirmDisabled}
        headerMsg={t('redeemLicenseCodeLabel')}
        initHeight={modalHeight}
        initWidth={600}
        isConfirmButtonLoading={isLoading}
        isLoading={isLoading}
        isOpen={isModalOpen}
        onClose={handleClose}
        top={modalTop}
      />
    </>
  );
});

export default RedeemLicenseCodeButton;

SatCoreRegister('RedeemLicenseCodeButton', RedeemLicenseCodeButton);
